import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo, useState } from "react";
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { addDays, endOfDay, format, startOfDay } from "date-fns";
import { usePagination } from "hooks";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setCustomMessage,
  setOpen as setOpenCustomMessage
} from "components/action-snackbar/action-snackbar-slice";
import {
  setContent,
  setOnChange,
  setOpen,
  setTitle
} from "components/confirm-dialog/confirm-dialog.slice";
import { CustomFilterOption } from "components/custom-filters-list/custom-filters-list.component";
import type { StatusTransitionCode } from "components/status-transition-cell/status-transition-cell.component";
import {
  STAFF_TASK_COLORS_FEATURE,
  TASK_TEMPLATE_RICH_TEXT_FEATURE,
  TEAMS_FEATURE,
  TEAM_MENTIONS_FEATURE
} from "constants/feature-flags";
import {
  MY_COMPLETED_TASKS,
  TASKS_ASSIGNED_TO_ME,
  TASKS_CREATED_BY_ME,
  type TASKS_TYPE,
  TASK_STATUSES,
  TASK_STATUS_COMPLETED,
  TASK_STATUS_IN_PROGRESS,
  TASK_STATUS_TODO
} from "constants/staff-tasks";
import { selectCurrentUser } from "features/auth/auth-slice";
import {
  staffTasksApi,
  useCreateStaffTaskMutation,
  useDeleteStaffTaskMutation,
  useGetStaffTasksGroupByPriorityQuery,
  useGetStaffTasksQuery,
  useQueryStaffTasksCounterQuery,
  useUpdateStaffTaskMutation
} from "services/staff-tasks";
import {
  StaffTask,
  StaffTasksFilter,
  type StaffTasksGroupByPriorityResponse
} from "services/types/staff-tasks.type";

import StaffTaskDeepLink from "../components/staff-task-deep-link.component";
import StaffTaskListModalDialog from "../components/staff-task-list-modal-dialog";
import StaffTaskModalDialog from "../components/staff-task-modal-dialog";
import useTasksV2CustomFilters, {
  PatientTasksCustomFiltersValues
} from "../filters/use-tasks-v2-custom-filters.hook";
import {
  type StaffTaskV2FormValues,
  formatStaffTaskToInput,
  shouldPreserveDescriptionHtml
} from "../staff-task.utils";
import { GROUP_BY_PRIORITY_QUERY_WITH_TEAM } from "../utils/team-list-query";

type DateRange = {
  before_due_at: string | null;
  after_due_at: string | null;
};

type TaskDrawerMode = "view" | "create" | undefined;

// Optional values to prefill the create-task form when the drawer is opened
// programmatically (e.g. from the Alerts report "Create Task" action).
export type TaskPrefill = {
  title?: string;
  description?: string;
  due_at?: string;
  assignee?: { label: string; value: string };
};

type TaskModalMode = {
  displayMode: "view" | "edit" | "create" | undefined;
  onEditCancelMode: "closeModal" | "openViewModal";
  ignoreTaskPreviewAfterCreation?: boolean;
};

const StaffTasksV2Context = React.createContext<{
  fetchMore: () => void;
  isBusy: boolean;
  isDrawerDataBusy: boolean;
  patientTaskList: StaffTask[];
  tasksList: StaffTask[];
  totalTasksCount: number;
  patientTasksGroupByPriority: StaffTasksGroupByPriorityResponse;

  //
  patientCustomFilters: CustomFilterOption[];
  patientTaskFilters: StaffTasksFilter;
  patientSelectedCustomFilters: PatientTasksCustomFiltersValues;
  setPatientTaskFilters: React.Dispatch<React.SetStateAction<StaffTasksFilter>>;
  updatePatientDateRange: React.Dispatch<React.SetStateAction<DateRange>>;
  patient_before_due_at: string | null;
  patient_after_due_at: string | null;

  customFilters: CustomFilterOption[];
  taskFilters: StaffTasksFilter;
  selectedCustomFilters: PatientTasksCustomFiltersValues;
  setTaskFilters: React.Dispatch<React.SetStateAction<StaffTasksFilter>>;
  setSelectedCustomFilters: React.Dispatch<
    React.SetStateAction<PatientTasksCustomFiltersValues>
  >;
  updateDateRange: React.Dispatch<React.SetStateAction<DateRange>>;
  before_due_at: string | null;
  after_due_at: string | null;
  //
  refetch: () => void;
  //
  patientId?: number;
  paramsPatientId?: number;
  setPatientId: React.Dispatch<React.SetStateAction<number | undefined>>;
  openTaskListModal: boolean;
  handleOpenTaskListModal: () => void;
  handleCloseTaskListModal: () => void;
  isPatientViewMode: boolean;
  setIsPatientViewMode: React.Dispatch<React.SetStateAction<boolean>>;
  taskDrawerMode: TaskDrawerMode;
  setTaskDrawerMode: React.Dispatch<React.SetStateAction<TaskDrawerMode>>;
  taskModalMode: TaskModalMode;
  setTaskModalMode: React.Dispatch<React.SetStateAction<TaskModalMode>>;
  currentTaskId?: number | undefined;
  setCurrentTaskId: React.Dispatch<React.SetStateAction<number | undefined>>;
  //
  handleChangeTaskOrdering: (type: "patient" | "clinic", field: string) => void;
  taskListOrdering: string | undefined;
  patientTaskOrdering: string | undefined;
  //
  isGroupByPriority: boolean;
  taskPrefill?: TaskPrefill;
  handleOpenTaskDrawer: (
    mode: "view" | "create",
    patientId?: number | undefined,
    prefill?: TaskPrefill
  ) => void;
  handleCloseTaskDrawer: () => void;
  handleOpenTaskModal: (params: {
    mode: "view" | "edit" | "create";
    taskId?: number | undefined;
    onCancelMode?: "closeModal" | "openViewModal" | undefined;
    ignoreTaskPreviewAfterCreation?: boolean;
  }) => void;
  handleCloseTaskModal: () => void;
  //
  isUpdating?: boolean;
  handleDelete: (task: StaffTask, callback?: () => void) => void;

  handleCreate: (
    data: StaffTaskV2FormValues,
    callback?: (createdTaskId?: number) => void
  ) => void;

  handleStatusUpdate: (
    id: number,
    status: StatusTransitionCode
  ) => Promise<void>;

  handleUpdate: (data: StaffTaskV2FormValues, callback?: () => void) => void;
  //
  tasksAssignedToMe: StaffTasksGroupByPriorityResponse;
  tasksCreatedByMe: StaffTasksGroupByPriorityResponse;
  myCompletedTasks: StaffTasksGroupByPriorityResponse;
  clearTaskListData: () => void;
  taskViewType: TASKS_TYPE;
  setTaskViewType: React.Dispatch<React.SetStateAction<TASKS_TYPE>>;
  tabBaseFilters: Record<TASKS_TYPE, StaffTasksFilter>;
  includeCreatedCompleted: boolean;
  setIncludeCreatedCompleted: React.Dispatch<React.SetStateAction<boolean>>;
} | null>(null);

type StaffTasksV2ProviderProps = {
  children?: React.ReactNode;
};

const DEFAULT_STATUSES = TASK_STATUSES.getOptions().filter(
  (option) =>
    Number(option.value) === TASK_STATUS_TODO ||
    Number(option.value) === TASK_STATUS_IN_PROGRESS
);

export function StaffTasksV2Provider({ children }: StaffTasksV2ProviderProps) {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  const flags = useFlags();
  const enableTaskRichText = Boolean(flags[TASK_TEMPLATE_RICH_TEXT_FEATURE]);
  const enableTaskColors = Boolean(flags[STAFF_TASK_COLORS_FEATURE]);
  const enableMentions = Boolean(flags[TEAM_MENTIONS_FEATURE]);
  // Teams: `team` goes in the task payload only with the flag on (an older backend 400s the key).
  const enableTeams = Boolean(flags[TEAMS_FEATURE]);
  // The lists name the team pill only with the flag on (an older backend 400s the RestQL key).
  const listQuery = enableTeams ? GROUP_BY_PRIORITY_QUERY_WITH_TEAM : undefined;

  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const paramsPatientId = params
    ? Number(params["*"]?.split("/")[0]) || undefined
    : undefined;

  const [patientId, setPatientId] = useState<number | undefined>(
    paramsPatientId ? Number(paramsPatientId) : undefined
  );

  const [isPatientViewMode, setIsPatientViewMode] = useState(false);

  const [openTaskListModal, setOpenTaskListModal] = useState(false);

  const [taskDrawerMode, setTaskDrawerMode] =
    useState<TaskDrawerMode>(undefined);

  const [taskPrefill, setTaskPrefill] = useState<TaskPrefill | undefined>();

  const isTasksRoute = location.pathname.includes("/tasks");
  const shouldFetchPatientTasks = isTasksRoute || Boolean(taskDrawerMode);

  const [taskModalMode, setTaskModalMode] = useState<TaskModalMode>({
    displayMode: undefined,
    onEditCancelMode: "closeModal"
  });

  const [currentTaskId, setCurrentTaskId] = useState<number | undefined>();

  const [taskViewType, setTaskViewType] =
    useState<TASKS_TYPE>(TASKS_ASSIGNED_TO_ME);

  const [includeCreatedCompleted, setIncludeCreatedCompleted] = useState(false);

  const handleOpenTaskDrawer = useCallback(
    (
      mode: "view" | "create",
      patientId?: number | undefined,
      prefill?: TaskPrefill
    ) => {
      setTaskDrawerMode(mode);
      setTaskPrefill(prefill);
      setCurrentTaskId(undefined);
      setTaskModalMode({
        displayMode: undefined,
        onEditCancelMode: "closeModal"
      });
      setIsPatientViewMode(Boolean(patientId));
      setTaskViewType(TASKS_ASSIGNED_TO_ME);

      setPatientId(patientId);
    },
    []
  );

  const handleCloseTaskDrawer = useCallback(() => {
    setTaskDrawerMode(undefined);
    setTaskPrefill(undefined);
    setCurrentTaskId(undefined);
    setTaskModalMode({
      displayMode: undefined,
      onEditCancelMode: "closeModal"
    });
  }, []);

  const handleOpenTaskModal = useCallback(
    (params: {
      mode: "view" | "edit" | "create";
      taskId?: number | undefined;
      onCancelMode?: "closeModal" | "openViewModal" | undefined;
      ignoreTaskPreviewAfterCreation?: boolean;
    }) => {
      const {
        mode,
        taskId,
        onCancelMode = "closeModal",
        ignoreTaskPreviewAfterCreation
      } = params;
      setCurrentTaskId(mode === "create" ? undefined : taskId);
      setTaskModalMode({
        displayMode: mode,
        onEditCancelMode: onCancelMode,
        ignoreTaskPreviewAfterCreation
      });
    },
    []
  );

  const [taskListOrdering, setTaskListOrdering] = useState<string | undefined>(
    "-created_at"
  );

  const [patientTaskOrdering, setPatientTaskOrdering] = useState<
    string | undefined
  >("-created_at");

  const handleChangeTaskOrdering = useCallback(
    (type: "patient" | "clinic", field: string) => {
      const currentValue =
        type === "patient" ? patientTaskOrdering : taskListOrdering;

      let newValue: string = "";

      if (currentValue && !currentValue.includes(field)) {
        newValue = field;
      } else if (currentValue?.startsWith("-")) {
        newValue = "";
      } else if (currentValue && !currentValue.startsWith("-")) {
        newValue = `-${field}`;
      } else if (!currentValue) {
        newValue = field;
      } else {
        newValue = "";
      }

      if (type === "patient") {
        setPatientTaskOrdering(newValue);
      } else if (type === "clinic") {
        setTaskListOrdering(newValue);
      }
    },
    [patientTaskOrdering, taskListOrdering]
  );

  const handleCloseTaskModal = useCallback(() => {
    setCurrentTaskId(undefined);
    setTaskModalMode({
      displayMode: undefined,
      onEditCancelMode: "closeModal"
    });

    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has("taskId")) {
      searchParams.delete("taskId");
      const newSearch = searchParams.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ``}`;
      navigate(newUrl, { replace: true });
    }
  }, [location, navigate]);

  const handleOpenTaskListModal = useCallback(() => {
    setOpenTaskListModal(true);
  }, []);

  const handleCloseTaskListModal = useCallback(() => {
    setOpenTaskListModal(false);
  }, []);

  const [
    {
      before_due_at: patient_before_due_at,
      after_due_at: patient_after_due_at
    },
    updatePatientDateRange
  ] = useState<DateRange>({
    before_due_at: null,
    after_due_at: null
  });

  const [{ before_due_at, after_due_at }, updateDateRange] =
    useState<DateRange>({
      before_due_at: null,
      after_due_at: null
    });

  const [patientSelectedCustomFilters, setPatientSelectedCustomFilters] =
    useState<PatientTasksCustomFiltersValues>({
      status: [],
      group_by_priority: false
    });

  const [selectedCustomFilters, setSelectedCustomFilters] =
    useState<PatientTasksCustomFiltersValues>({
      status: DEFAULT_STATUSES,
      group_by_priority: false
    });

  const [patientTaskFilters, setPatientTaskFilters] =
    useState<StaffTasksFilter>({
      status: []
    });

  const [taskFilters, setTaskFilters] = useState<StaffTasksFilter>({
    status: [TASK_STATUS_TODO, TASK_STATUS_IN_PROGRESS]
  });

  useEffect(() => {
    setPatientTaskFilters((prev) => ({
      ...prev,
      patient: patientId ? Number(patientId) : undefined,
      before_due_at: patient_before_due_at
        ? format(addDays(new Date(patient_before_due_at), 1), "yyyy-MM-dd")
        : undefined,
      after_due_at: patient_after_due_at
        ? format(new Date(patient_after_due_at), "yyyy-MM-dd")
        : undefined,
      ordering: patientTaskOrdering
    }));
  }, [
    setPatientTaskFilters,
    patient_before_due_at,
    patient_after_due_at,
    patientId,
    patientTaskOrdering
  ]);

  useEffect(() => {
    setTaskFilters((prev) => ({
      ...prev,
      patient: undefined,
      before_due_at: before_due_at
        ? format(addDays(new Date(before_due_at), 1), "yyyy-MM-dd")
        : undefined,
      after_due_at: after_due_at
        ? format(new Date(after_due_at), "yyyy-MM-dd")
        : undefined,
      ordering: taskListOrdering
    }));
  }, [after_due_at, before_due_at, taskListOrdering]);

  let isGroupingEnabled = Boolean(patientTaskFilters.group_by_priority);

  if (isGroupingEnabled && !(patientId || paramsPatientId)) {
    isGroupingEnabled = false;
  }

  const patientTaskFetchFilters = useMemo(
    () => ({
      ...patientTaskFilters,
      patient: patientId || paramsPatientId,
      group_by_priority: isGroupingEnabled,
      ...(listQuery && isGroupingEnabled ? { query: listQuery } : {})
    }),
    [
      patientTaskFilters,
      isGroupingEnabled,
      paramsPatientId,
      patientId,
      listQuery
    ]
  );

  const {
    data: patientTaskList = [],
    isLoading: isPatientTaskListLoading,
    clearData: clearPatientTasksListData,
    isUninitialized: isPatientTaskListUninitialized,
    fetchMore: fetchMorePatientTaskList
  } = usePagination<StaffTasksFilter, StaffTask>({
    useQuery: useGetStaffTasksQuery,
    limit: 25,
    params: patientTaskFetchFilters,
    options: {
      skip:
        isGroupingEnabled ||
        !shouldFetchPatientTasks ||
        (!patientId && !paramsPatientId)
    }
  });

  const {
    data: tasksList = [],
    isLoading: isTaskListLoading,
    clearData: clearTaskListData,
    isUninitialized: isTaskListUninitialized,
    fetchMore: fetchMoreTaskList,
    refetch: refetchTasksList
  } = usePagination<StaffTasksFilter, StaffTask>({
    useQuery: useGetStaffTasksQuery,
    limit: 25,
    params: taskFilters,
    options: {
      skip: isGroupingEnabled || !openTaskListModal
    }
  });

  const { data: totalTasksCount = 0 } = useQueryStaffTasksCounterQuery(
    taskFilters,
    {
      skip: isGroupingEnabled || !openTaskListModal
    }
  );

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (
      isTaskListUninitialized &&
      tasksList.length === 0 &&
      taskFilters.created_from_bulk_action
    ) {
      interval = setInterval(() => {
        refetchTasksList();
      }, 3000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [tasksList, taskFilters, refetchTasksList, isTaskListUninitialized]);

  const {
    data: patientTasksGroupByPriority = {
      totalCount: 0,
      items: {}
    },
    isFetching: isFetchingTasksGroupByPriority,
    isLoading: isLoadingTasksGroupByPriority,
    isUninitialized: isUninitializedTasksGroupByPriority
  } = useGetStaffTasksGroupByPriorityQuery(patientTaskFetchFilters, {
    skip: !isGroupingEnabled
  });

  const updatePatientCustomFilter = useCallback((field: string, value: any) => {
    return setPatientTaskFilters((prevFilters) => ({
      ...prevFilters,
      [field]: value
    }));
  }, []);

  const updateCustomFilter = useCallback((field: string, value: any) => {
    return setTaskFilters((prevFilters) => ({
      ...prevFilters,
      [field]: value
    }));
  }, []);

  const patientCustomFilters = useTasksV2CustomFilters({
    updateCustomFilter: updatePatientCustomFilter,
    selectedCustomFilters: patientSelectedCustomFilters,
    setSelectedCustomFilters: setPatientSelectedCustomFilters
  });

  const customFilters = useTasksV2CustomFilters({
    updateCustomFilter,
    selectedCustomFilters,
    setSelectedCustomFilters
  });

  const fetchMore = useCallback(() => {
    if (openTaskListModal) {
      fetchMoreTaskList();
    } else if (!isGroupingEnabled) {
      fetchMorePatientTaskList();
    }
  }, [
    fetchMorePatientTaskList,
    fetchMoreTaskList,
    isGroupingEnabled,
    openTaskListModal
  ]);

  // Skip drawer data loading when drawer is not open or user is not authenticated
  const drawerDataSkipCondition = useMemo(
    () => currentUser.id === -1 || taskDrawerMode === undefined,
    [currentUser.id, taskDrawerMode]
  );

  const {
    data: tasksAssignedToMe = {
      totalCount: 0,
      items: {}
    },
    isFetching: isFetchingTasksAssignedToMe,
    isLoading: isLoadingTasksAssignedToMe,
    isUninitialized: isUserTasksUnInitialized,
    refetch: refetchUserTasks
  } = useGetStaffTasksGroupByPriorityQuery(
    {
      assignee: currentUser.id,
      patient: isPatientViewMode ? patientId : undefined,
      is_active: true,
      is_complete: false,
      status: [TASK_STATUS_TODO, TASK_STATUS_IN_PROGRESS],
      query: listQuery
    },
    {
      skip: drawerDataSkipCondition || taskViewType !== TASKS_ASSIGNED_TO_ME
    }
  );

  const createdByMeStatuses = useMemo(
    () =>
      includeCreatedCompleted
        ? [TASK_STATUS_TODO, TASK_STATUS_IN_PROGRESS, TASK_STATUS_COMPLETED]
        : [TASK_STATUS_TODO, TASK_STATUS_IN_PROGRESS],
    [includeCreatedCompleted]
  );

  const {
    data: tasksCreatedByMe = {
      totalCount: 0,
      items: {}
    },
    isFetching: isFetchingTasksCreatedByMe,
    isLoading: isLoadingTasksCreatedByMe,
    isUninitialized: isMyCreatedTasksUninitialized,
    refetch: refetchMyCreatedTasks
  } = useGetStaffTasksGroupByPriorityQuery(
    {
      created_by: currentUser.id,
      patient: isPatientViewMode ? patientId : undefined,
      ordering: "-created_at",
      is_active: true,
      status: createdByMeStatuses,
      query: listQuery
    },
    {
      skip: drawerDataSkipCondition || taskViewType !== TASKS_CREATED_BY_ME
    }
  );

  const {
    data: myCompletedTasks = {
      totalCount: 0,
      items: {}
    },
    isFetching: isFetchingMyCompletedTasks,
    isLoading: isLoadingMyCompletedTasks,
    isUninitialized: isMyCompletedTasksUninitialized,
    refetch: refetchMyCompletedTasks
  } = useGetStaffTasksGroupByPriorityQuery(
    {
      assignee: currentUser.id,
      is_complete: true,
      patient: isPatientViewMode ? patientId : undefined,
      is_active: true,
      query: listQuery
    },
    {
      skip: drawerDataSkipCondition || taskViewType !== MY_COMPLETED_TASKS
    }
  );

  //
  const refetchDrawerData = useCallback(() => {
    if (!isUserTasksUnInitialized && !drawerDataSkipCondition) {
      refetchUserTasks();
    }
    if (!isMyCreatedTasksUninitialized && !drawerDataSkipCondition) {
      refetchMyCreatedTasks();
    }
    if (!isMyCompletedTasksUninitialized && !drawerDataSkipCondition) {
      refetchMyCompletedTasks();
    }
  }, [
    isMyCompletedTasksUninitialized,
    isMyCreatedTasksUninitialized,
    isUserTasksUnInitialized,
    drawerDataSkipCondition,
    refetchMyCompletedTasks,
    refetchMyCreatedTasks,
    refetchUserTasks
  ]);

  const refetch = useCallback(() => {
    dispatch(staffTasksApi.util.invalidateTags(["StaffTasks"]));
    clearPatientTasksListData();
    clearTaskListData();
    if (!isTaskListUninitialized && !isGroupingEnabled && openTaskListModal) {
      refetchTasksList();
    }
    refetchDrawerData();
  }, [
    clearPatientTasksListData,
    clearTaskListData,
    refetchTasksList,
    dispatch,
    refetchDrawerData,
    isTaskListUninitialized,
    isGroupingEnabled,
    openTaskListModal
  ]);

  const [deleteStaffTask] = useDeleteStaffTaskMutation();
  const [updateStaffTask, { isLoading: isUpdating }] =
    useUpdateStaffTaskMutation();

  const [createStaffTask, { isLoading: isCreating }] =
    useCreateStaffTaskMutation();

  const handleDelete = useCallback(
    (task: StaffTask, callback?: () => void) => {
      dispatch(setTitle("Are you sure you want to delete this task?"));
      dispatch(
        setOnChange(() => {
          setPatientId(Number(task?.patient?.id));

          deleteStaffTask(task.id).then(() => {
            refetch();
            callback?.();
          });
        })
      );
      dispatch(setOpen(true));
      dispatch(setContent(`Delete "${task.title}"?`));
    },
    [dispatch, deleteStaffTask, refetch]
  );

  const handleCreate = useCallback(
    (
      data: StaffTaskV2FormValues,
      callback?: (createdTaskId?: number) => void
    ) => {
      setPatientId(Number(data?.patient?.id));
      createStaffTask(
        formatStaffTaskToInput(
          data,
          shouldPreserveDescriptionHtml(
            data.description,
            enableTaskRichText,
            enableMentions
          ),
          enableTaskColors,
          enableTeams
        )
      ).then((response) => {
        refetch();
        if (taskModalMode.ignoreTaskPreviewAfterCreation) {
          dispatch(setCustomMessage("Task was created successfully!"));
          dispatch(setOpenCustomMessage(true));
        } else {
          setTaskDrawerMode("view");
          // Land on the tab where the new task is actually visible. When it is
          // assigned to someone else (e.g. an Alert assigns the alert's creator)
          // it won't appear under "Assigned To Me", so show "Created By Me".
          const createdAssigneeId = Number(data?.assignee?.value);
          setTaskViewType(
            createdAssigneeId === currentUser.id
              ? TASKS_ASSIGNED_TO_ME
              : TASKS_CREATED_BY_ME
          );
        }
        const createdTaskId =
          "data" in response ? response.data?.id : undefined;
        callback?.(createdTaskId);
      });
    },
    [
      createStaffTask,
      refetch,
      taskModalMode.ignoreTaskPreviewAfterCreation,
      dispatch,
      enableTaskRichText,
      enableTaskColors,
      enableMentions,
      enableTeams,
      currentUser.id
    ]
  );

  const handleStatusUpdate = useCallback(
    (id: number, status: StatusTransitionCode) => {
      return updateStaffTask({ id, status: Number(status) }).then(() => {
        refetch();
      });
    },
    [refetch, updateStaffTask]
  );

  const handleUpdate = useCallback(
    (data: StaffTaskV2FormValues, callback?: () => void) => {
      setPatientId(Number(data?.patient?.id));
      updateStaffTask(
        formatStaffTaskToInput(
          data,
          shouldPreserveDescriptionHtml(
            data.description,
            enableTaskRichText,
            enableMentions
          ),
          enableTaskColors,
          enableTeams
        )
      ).then(() => {
        refetch();
        callback?.();
      });
    },
    [
      updateStaffTask,
      refetch,
      enableTaskRichText,
      enableTaskColors,
      enableMentions,
      enableTeams
    ]
  );
  //

  const isBusy = openTaskListModal
    ? isTaskListLoading || isTaskListUninitialized
    : isGroupingEnabled
      ? isFetchingTasksGroupByPriority ||
        isLoadingTasksGroupByPriority ||
        isUninitializedTasksGroupByPriority
      : isPatientTaskListLoading || isPatientTaskListUninitialized;

  const isDrawerDataBusy =
    (taskViewType === TASKS_ASSIGNED_TO_ME &&
      (isFetchingTasksAssignedToMe ||
        isLoadingTasksAssignedToMe ||
        isUserTasksUnInitialized)) ||
    (taskViewType === TASKS_CREATED_BY_ME &&
      (isFetchingTasksCreatedByMe ||
        isLoadingTasksCreatedByMe ||
        isMyCreatedTasksUninitialized)) ||
    (taskViewType === MY_COMPLETED_TASKS &&
      (isFetchingMyCompletedTasks ||
        isLoadingMyCompletedTasks ||
        isMyCompletedTasksUninitialized));

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (
      Object.keys(flags).length > 0 &&
      location.pathname !== "/" &&
      searchParams.has(`taskId`)
    ) {
      const taskId = searchParams.get(`taskId`);
      handleOpenTaskModal({ mode: "view", taskId: Number(taskId) });
    }
  }, [handleOpenTaskModal, location.search, location.pathname, flags]);

  // Deep link from the daily reminder email ("See all my tasks"): /?taskList=today
  // opens the Clinic Tasks List modal filtered to tasks due today with the signed-in
  // user preselected as the assignee. The param is consumed (stripped from the URL)
  // so closing the modal doesn't reopen it.
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (
      location.pathname === "/" ||
      !searchParams.has("taskList") ||
      !currentUser?.id
    ) {
      return;
    }

    const today = new Date();
    updateDateRange({
      after_due_at: startOfDay(today).toISOString(),
      before_due_at: endOfDay(today).toISOString()
    });
    setTaskFilters((prev) => ({ ...prev, assignee: currentUser.id }));
    setSelectedCustomFilters((prev) => ({
      ...prev,
      assignee: [
        {
          label: `${currentUser.first_name} ${currentUser.last_name}`.trim(),
          value: String(currentUser.id)
        }
      ]
    }));
    setOpenTaskListModal(true);

    searchParams.delete("taskList");
    const newSearch = searchParams.toString();
    navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ``}`, {
      replace: true
    });
  }, [location.search, location.pathname, currentUser, navigate]);

  const tabBaseFilters = useMemo(
    (): Record<TASKS_TYPE, StaffTasksFilter> => ({
      [TASKS_ASSIGNED_TO_ME]: {
        assignee: currentUser.id,
        patient: isPatientViewMode ? patientId : undefined,
        is_active: true,
        is_complete: false,
        status: [TASK_STATUS_TODO, TASK_STATUS_IN_PROGRESS]
      },
      [TASKS_CREATED_BY_ME]: {
        created_by: currentUser.id,
        patient: isPatientViewMode ? patientId : undefined,
        ordering: "-created_at",
        is_active: true,
        status: createdByMeStatuses
      },
      [MY_COMPLETED_TASKS]: {
        assignee: currentUser.id,
        is_complete: true,
        patient: isPatientViewMode ? patientId : undefined,
        is_active: true
      }
    }),
    [currentUser.id, isPatientViewMode, patientId, createdByMeStatuses]
  );

  const contextValue = useMemo(
    () => ({
      isBusy,
      isDrawerDataBusy,
      patientTasksGroupByPriority,
      tasksList,
      totalTasksCount: totalTasksCount || 0,
      patientTaskList,
      fetchMore,
      //
      patientCustomFilters,
      patientTaskFilters,
      patientSelectedCustomFilters,
      setPatientTaskFilters,
      updatePatientDateRange,
      patient_before_due_at,
      patient_after_due_at,
      //
      customFilters,
      taskFilters,
      selectedCustomFilters,
      setTaskFilters,
      setSelectedCustomFilters,
      updateDateRange,
      before_due_at,
      after_due_at,
      //
      refetch,
      //
      patientId,
      paramsPatientId,
      setPatientId,
      openTaskListModal,
      handleOpenTaskListModal,
      handleCloseTaskListModal,
      isPatientViewMode,
      setIsPatientViewMode,
      currentTaskId,
      setCurrentTaskId,
      taskDrawerMode,
      setTaskDrawerMode,
      taskModalMode,
      setTaskModalMode,
      //
      handleChangeTaskOrdering,
      taskListOrdering,
      patientTaskOrdering,
      //
      isGroupByPriority: isGroupingEnabled,
      taskPrefill,
      handleOpenTaskDrawer,
      handleCloseTaskDrawer,
      handleOpenTaskModal,
      handleCloseTaskModal,
      //
      isUpdating: isUpdating || isCreating,
      handleDelete,
      handleCreate,
      handleStatusUpdate,
      handleUpdate,
      //
      tasksAssignedToMe,
      tasksCreatedByMe,
      myCompletedTasks,
      clearTaskListData,
      taskViewType,
      setTaskViewType,
      tabBaseFilters,
      includeCreatedCompleted,
      setIncludeCreatedCompleted
    }),
    [
      isBusy,
      isDrawerDataBusy,
      patientTasksGroupByPriority,
      tasksList,
      totalTasksCount,
      patientTaskList,
      fetchMore,
      patientCustomFilters,
      patientTaskFilters,
      patientSelectedCustomFilters,
      setPatientTaskFilters,
      updatePatientDateRange,
      patient_before_due_at,
      patient_after_due_at,
      customFilters,
      taskFilters,
      selectedCustomFilters,
      setTaskFilters,
      setSelectedCustomFilters,
      updateDateRange,
      before_due_at,
      after_due_at,
      refetch,
      patientId,
      paramsPatientId,
      setPatientId,
      openTaskListModal,
      handleOpenTaskListModal,
      handleCloseTaskListModal,
      isPatientViewMode,
      setIsPatientViewMode,
      currentTaskId,
      setCurrentTaskId,
      taskDrawerMode,
      setTaskDrawerMode,
      taskModalMode,
      setTaskModalMode,
      handleChangeTaskOrdering,
      taskListOrdering,
      patientTaskOrdering,
      isGroupingEnabled,
      taskPrefill,
      handleOpenTaskDrawer,
      handleCloseTaskDrawer,
      handleOpenTaskModal,
      handleCloseTaskModal,
      isUpdating,
      isCreating,
      handleDelete,
      handleCreate,
      handleStatusUpdate,
      handleUpdate,
      tasksAssignedToMe,
      tasksCreatedByMe,
      myCompletedTasks,
      clearTaskListData,
      taskViewType,
      setTaskViewType,
      tabBaseFilters,
      includeCreatedCompleted
    ]
  );

  return (
    <StaffTasksV2Context.Provider value={contextValue}>
      {children}
      <StaffTaskListModalDialog />
      <StaffTaskModalDialog />
      {/* `?task=` comes only from a mention email: off with the flag, as before mentions. */}
      {enableMentions && <StaffTaskDeepLink />}
    </StaffTasksV2Context.Provider>
  );
}

export default function useStaffTasksV2() {
  const context = React.useContext(StaffTasksV2Context);
  if (!context) {
    throw new Error(
      `useStaffTasksV2 must be used within a StaffTasksV2Provider`
    );
  }

  return context;
}
